/**
 * La región del proyecto abierto, para todo el panel.
 *
 * Es un ajuste que se lee en muchos sitios y se cambia en uno: la moneda en la
 * que se crea cada precio la necesitan el asistente de ofertas, la ficha de una
 * oferta y el diálogo de servicios de la agenda; el formato de los importes lo
 * necesita cualquier pantalla que enseñe dinero. Pasarlo por props desde el
 * armazón hasta ahí abajo eran diez componentes intermedios que no lo usan.
 *
 * ── Por qué además hay una variable de módulo ───────────────────────────────
 * `formatMoney` no es un componente y se llama desde tablas, ejes de gráfica y
 * celdas donde no hay hooks. Se le podría pasar el locale en cada una de sus
 * ~20 llamadas, y el resultado garantizado sería que alguna se quedara sin
 * pasarlo y una pantalla escribiera los importes distinto que la de al lado —
 * que es justo el fallo que este ajuste existe para arreglar. Así que el
 * proveedor deja escrito el locale vigente y `formatMoney` lo lee de ahí.
 * Vale porque sólo hay un proyecto abierto a la vez y porque afecta únicamente
 * a CÓMO se escribe un número: la moneda de un importe siempre viaja con él.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getProjectRegion, type ProjectRegion } from './api'

/**
 * Lo que se supone mientras no se sabe: lo mismo que supone el servidor.
 *
 * `countryName` se queda en castellano a propósito y no pasa por el catálogo: el
 * valor bueno lo manda el servidor con la región del proyecto, y esto sólo se ve
 * el instante que tarda en contestar. Traducirlo aquí crearía dos versiones del
 * mismo dato —la nuestra y la suya— que se contradirían en cuanto llegara la
 * respuesta. Si el nombre del país tiene que hablar el idioma de quien mira, eso
 * se arregla en `getProjectRegion`, que es quien lo decide.
 */
export const DEFAULT_REGION: ProjectRegion = {
  countryCode: 'MX',
  countryName: 'México',
  currency: 'mxn',
  language: 'es',
  locale: 'es-MX',
  profile: 'mx',
}

/**
 * Qué cuerpo legal citan las plantillas de cada país, dicho sin prometer de más.
 *
 * Vive aquí y no en la pantalla de Región porque lo dicen dos sitios —donde se
 * elige el país y donde se escriben las políticas— y son la clase de frase que,
 * escrita dos veces, acaba diciendo «México» en una pantalla y otra cosa en la
 * de al lado. En un aviso legal eso no es una errata: es decirle a alguien que
 * su documento cita una ley que no cita.
 */
export const PROFILE_NOTE_KEY: Record<ProjectRegion['profile'], string> = {
  mx: 'region.profile.mx',
  es: 'region.profile.es',
  us: 'region.profile.us',
  generic: 'region.profile.generic',
}

let currentLocale = DEFAULT_REGION.locale

/**
 * El nombre de un país en el idioma del panel.
 *
 * El servidor manda `countryName` en español —es el de su lista—, y en un panel
 * en inglés «Estados Unidos» se leía como un descuido. `Intl` lo sabe decir en
 * cualquier idioma a partir del código.
 */
export function regionName(countryCode: string, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(countryCode) ?? countryCode
  } catch {
    return countryCode
  }
}

/** El locale vigente. Lo lee `formatMoney`, que no puede usar hooks. */
export function moneyLocale(): string {
  return currentLocale
}

type RegionValue = ProjectRegion & {
  /** Cierto mientras se carga: sirve para no enseñar una moneda equivocada. */
  loading: boolean
  /** Lo llama la pantalla de Región tras guardar, para que todo se entere. */
  refresh: () => Promise<void>
}

const RegionContext = createContext<RegionValue>({
  ...DEFAULT_REGION,
  loading: false,
  refresh: async () => {},
})

export function useRegion(): RegionValue {
  return useContext(RegionContext)
}

export function RegionProvider({ projectId, token, children }: {
  projectId: string
  token: string
  children: React.ReactNode
}) {
  const [region, setRegion] = useState<ProjectRegion>(DEFAULT_REGION)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const data = await getProjectRegion(token, projectId)
      const next: ProjectRegion = {
        countryCode: data.countryCode,
        countryName: data.countryName,
        currency: data.currency,
        language: data.language,
        locale: data.locale,
        profile: data.profile,
      }
      currentLocale = next.locale
      setRegion(next)
    } catch {
      /* Un ajuste que no carga no puede dejar el panel en blanco: se sigue con
         el default, que es el mismo que aplica el servidor, y el importe se
         enseña con su propia moneda de todas formas. */
      currentLocale = DEFAULT_REGION.locale
      setRegion(DEFAULT_REGION)
    } finally {
      setLoading(false)
    }
  }, [projectId, token])

  useEffect(() => {
    setLoading(true)
    void load()
  }, [load])

  const value = useMemo<RegionValue>(
    () => ({ ...region, loading, refresh: load }),
    [region, loading, load],
  )

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>
}
